// src/hooks/usePlaid.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// ============= TYPES =============

interface PlaidAccount {
  account_id: string;
  name: string;
  mask: string;
  type: string;
  subtype: string;
  balance: number | null;
  available: number | null;
  currency: string | null;
}

interface LinkedBank {
  itemId: string;
  bankName: string;
  lastSynced: Date | string;
  accounts: PlaidAccount[];
  error?: string;
}

interface LinkTokenResponse {
  link_token: string;
  expiration: string;
}

interface ExchangeTokenResponse {
  success: boolean;
  bankName: string;
  itemId: string;
  accountCount: number;
}

interface TransactionSyncResponse {
  success: boolean;
  transactionCount: number;
  incomesCreated: number;
  expensesCreated: number;
  message: string;
}

interface AccountsResponse {
  accounts: LinkedBank[];
  message?: string;
  upgradeRequired?: boolean;
  currentTier?: 'free' | 'standard' | 'premium';
}

// ============= API HELPERS (TOKEN-PASSED) =============

const buildAuthHeaders = (token?: string | null) => {
  if (!token) return undefined;
  return { Authorization: `Bearer ${token}` };
};

const fetchLinkToken = async (token?: string | null): Promise<LinkTokenResponse> => {
  if (!token) throw new Error('Authentication required to fetch link token');
  const { data } = await axios.post(`${API_URL}/api/link-token`, {}, { headers: buildAuthHeaders(token) });
  return data;
};

const exchangePublicToken = async (params: { publicToken: string; token?: string | null }): Promise<ExchangeTokenResponse> => {
  const { publicToken, token } = params;
  if (!token) throw new Error('Authentication required to exchange public token');
  const { data } = await axios.post(
    `${API_URL}/api/exchange-public-token`,
    { public_token: publicToken },
    { headers: buildAuthHeaders(token) }
  );
  return data;
};

const fetchAccounts = async (token?: string | null): Promise<AccountsResponse> => {
  if (!token) throw new Error('Authentication required to fetch accounts');
  const { data } = await axios.get<AccountsResponse>(`${API_URL}/api/accounts`, {
    headers: buildAuthHeaders(token),
  });
  return data;
};

const syncTransactions = async (token?: string | null): Promise<TransactionSyncResponse> => {
  if (!token) throw new Error('Authentication required to sync transactions');
  const { data } = await axios.get(`${API_URL}/api/transactions`, {
    headers: buildAuthHeaders(token),
  });
  return data;
};

const unlinkAccount = async (params: { itemId: string; token?: string | null }): Promise<{ success: boolean; message: string }> => {
  const { itemId, token } = params;
  if (!token) throw new Error('Authentication required to unlink account');
  const { data } = await axios.delete(`${API_URL}/api/accounts/${itemId}`, {
    headers: buildAuthHeaders(token),
  });
  return data;
};

// ============= HOOKS =============

/**
 * NOTE:
 * - All hooks below ALWAYS call React Query hooks (so hook order stays stable).
 * - Queries are disabled with `enabled: Boolean(token)` to avoid running without auth.
 * - Mutations validate token at mutation call-time and throw if missing.
 */

/** Link token (used to open Plaid Link) */
export const useLinkToken = (token?: string | null) => {
  return useQuery<LinkTokenResponse, Error>({
    queryKey: ['plaidLinkToken'],
    queryFn: () => fetchLinkToken(token),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    enabled: Boolean(token), // won't run until token exists
  });
};

/** Exchange public_token -> access token (mutation) */
export const useExchangeToken = (token?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (publicToken: string) => {
      return exchangePublicToken({ publicToken, token });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plaidAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['incomes'] });
    },
  });
};

/** Get Plaid accounts for the current user */
export const usePlaidAccounts = (token?: string | null) => {
  return useQuery<AccountsResponse, Error>({
    queryKey: ['plaidAccounts', token],
    queryFn: () => fetchAccounts(token),
    staleTime: 2 * 60 * 1000,
    retry: false,
    enabled: Boolean(token), // Query will be defined but only executed when token exists
    // Ensure stable return shape in select if needed
    select: (data) => ({
      ...data,
      upgradeRequired: data.upgradeRequired ?? false,
      currentTier: data.currentTier ?? 'free',
    }),
  });
};

/** Trigger a sync of transactions */
export const useSyncTransactions = (token?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return syncTransactions(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plaidAccounts'] });
      queryClient.invalidateQueries({ queryKey: ['expenses'] });
      queryClient.invalidateQueries({ queryKey: ['incomes'] });
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
};

/** Unlink an account */
export const useUnlinkAccount = (token?: string | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (itemId: string) => {
      return unlinkAccount({ itemId, token });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plaidAccounts'] });
    },
  });
};
